import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BotAnalyticsPanel from "@/components/BotAnalyticsPanel";
import BotTradeHistory from "@/components/BotTradeHistory";
import { Zap, ArrowRight, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BotShare — publicly accessible (no wallet required) bot analytics view.
 * URL: /bot/share/:botId — paired with the OG-tagged backend route at
 * /api/share/bot/:botId which is what users actually post on X / Discord.
 */
export default function BotShare() {
  const { botId } = useParams();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/prediction-bots/public`);
        const data = await res.json();
        const found = (data?.bots || []).find((b) => b.id === botId);
        if (active) setBot(found || null);
      } catch {
        // silent
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [botId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">
        Loading bot…
      </div>
    );
  }
  if (!bot) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="text-xl font-heading">Bot not found</h1>
        <p className="text-sm text-muted-foreground">This bot may have been removed.</p>
        <Link to="/" className="text-secondary hover:underline">Back to STXOSHI →</Link>
      </div>
    );
  }

  const pnl = bot.total_pnl_pct || 0;
  return (
    <div className="min-h-screen bg-background">
      {/* Minimal public header (no Connect Wallet — this page is unauthenticated) */}
      <header className="border-b border-border/40 px-4 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="font-heading font-bold tracking-wider text-orange-400"
          data-testid="share-brand"
        >
          STXOSHI
        </Link>
        <Link to="/" data-testid="share-cta">
          <Button size="sm" variant="outline">
            Deploy your own bot
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </header>

      <main className="p-4 md:p-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Hero */}
          <Card className="glass-card border-border" data-testid="share-hero">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-secondary" />
                <h1 className="font-heading text-xl font-bold">Bot {bot.name || botId.slice(-6)}</h1>
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    bot.status === "running"
                      ? "bg-green-500/20 text-green-400 border-green-500/30"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {(bot.status || "stopped").toUpperCase()}
                </Badge>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span
                  className={`font-mono text-4xl font-bold ${
                    pnl >= 0 ? "text-green-400" : "text-red-400"
                  }`}
                  data-testid="share-headline-pnl"
                >
                  {pnl >= 0 ? "+" : ""}
                  {pnl.toFixed(2)}%
                </span>
                <span className="text-sm text-muted-foreground">
                  {(bot.total_trades || 0).toLocaleString()} trades ·{" "}
                  {(bot.win_rate || 0).toFixed(1)}% win rate
                </span>
              </div>
              <a
                href={`${API}/api/share/bot/${botId}/og.png`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-secondary mt-3"
                data-testid="share-og-link"
              >
                <ExternalLink className="w-3 h-3" />
                View social card image
              </a>
            </CardContent>
          </Card>

          <BotAnalyticsPanel botId={botId} />
          <BotTradeHistory botId={botId} />

          <Card className="border-orange-500/30 bg-orange-500/5">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-muted-foreground mb-3">
                Want to deploy your own AI bot like this?
              </p>
              <Link to="/" data-testid="share-bottom-cta">
                <Button>
                  Get started on STXOSHI
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
